// Digital Footprint Score — pure, client-safe.
// Rates how exposed a subject is online across five categories and rolls the
// result into a credit-score style number (300–850, higher = less exposed).

export type FootprintCategoryId =
  | "breach"
  | "social"
  | "infrastructure"
  | "financial"
  | "metadata";

export type FootprintSignal = {
  label: string;
  detail: string;
  /** 0–100 exposure contributed by this signal (before weighting). */
  weight: number;
  source: string;
};

export type FootprintCategory = {
  id: FootprintCategoryId;
  label: string;
  blurb: string;
  /** 0–100, higher = more exposed. */
  exposure: number;
  grade: string;
  signals: FootprintSignal[];
  /** True when no collector covered this category at all. */
  unmeasured: boolean;
  advice: string;
};

export type FootprintScore = {
  /** 300–850, higher = safer / less exposed. */
  score: number;
  /** 0–100 exposure, the inverse view. */
  exposure: number;
  grade: string;
  band: "minimal" | "low" | "moderate" | "elevated" | "severe";
  headline: string;
  categories: FootprintCategory[];
  coverage: number;
};

type Collectorish = {
  source: string;
  status?: string;
  summary?: string;
  data?: unknown;
};

const CATEGORY_META: Record<FootprintCategoryId, { label: string; blurb: string; weight: number; advice: string }> = {
  breach: {
    label: "Breach exposure",
    blurb: "Credentials and personal records circulating in known data breaches.",
    weight: 0.3,
    advice: "Rotate reused passwords, enable MFA everywhere and monitor the affected addresses continuously.",
  },
  social: {
    label: "Social exposure",
    blurb: "Publicly discoverable profiles, handles, posts and press mentions.",
    weight: 0.2,
    advice: "Audit public profiles, prune legacy accounts and tighten what is indexable by search engines.",
  },
  infrastructure: {
    label: "Infrastructure exposure",
    blurb: "Domains, hosts, certificates, open services and DNS surface attributable to the subject.",
    weight: 0.2,
    advice: "Reduce the externally reachable surface, retire stale hosts and enforce registrar/DNS hardening.",
  },
  financial: {
    label: "Financial exposure",
    blurb: "Corporate filings, ownership records, wallets, sanctions and litigation footprint.",
    weight: 0.15,
    advice: "Review which financial and ownership records are public by obligation versus by accident.",
  },
  metadata: {
    label: "Metadata exposure",
    blurb: "Leaked identifiers in documents, commits, WHOIS, headers and technical artefacts.",
    weight: 0.15,
    advice: "Strip document metadata, scrub commit author data and use registrar privacy where lawful.",
  },
};

type Rule = {
  cat: FootprintCategoryId;
  re: RegExp;
  label: string;
  /** exposure contributed when this collector returns data */
  weight: number;
  /** extra exposure per counted item, capped by weight */
  perItem?: number;
};

const RULES: Rule[] = [
  { cat: "breach", re: /HaveIBeenPwned breaches/i, label: "Breached accounts", weight: 35, perItem: 12 },
  { cat: "breach", re: /HIBP pastes/i, label: "Email found in public paste dumps", weight: 20 },
  { cat: "breach", re: /paste|leak|Pwned pastes/i, label: "Paste-site leak references", weight: 20, perItem: 8 },
  { cat: "breach", re: /GitHub (secret|leak)|secret scan/i, label: "Secrets found in public code", weight: 30 },

  { cat: "social", re: /GitHub|GitLab|Bitbucket|Keybase|Reddit|Mastodon|DEV\.to|Hacker News|profile/i, label: "Public platform profiles", weight: 18, perItem: 6 },
  { cat: "social", re: /X search|Twitter|social/i, label: "Social posting footprint", weight: 15, perItem: 5 },
  { cat: "social", re: /news|GDELT|media|press|Wikipedia|Wikidata/i, label: "Media and encyclopaedic mentions", weight: 16, perItem: 4 },
  { cat: "social", re: /username|handle/i, label: "Handle reuse across platforms", weight: 18, perItem: 6 },

  { cat: "infrastructure", re: /RDAP|WHOIS|DNS |domain/i, label: "Attributable domains", weight: 18, perItem: 5 },
  { cat: "infrastructure", re: /Certificate Transparency|crt\.sh|CertSpotter/i, label: "Certificate transparency subdomains", weight: 20, perItem: 3 },
  { cat: "infrastructure", re: /passive DNS|Shodan|BGP|RIPE|IP geolocation|Tor exit/i, label: "Reachable hosts and network records", weight: 22, perItem: 5 },
  { cat: "infrastructure", re: /URLhaus|PhishStats|OTX reputation|malware|threat/i, label: "Hostile infrastructure associations", weight: 30 },
  { cat: "infrastructure", re: /SPF posture|security\.txt|HTTP headers/i, label: "Mail/web posture disclosure", weight: 10 },

  { cat: "financial", re: /OpenCorporates|GLEIF|Companies House|registry|SEC EDGAR|filing/i, label: "Corporate and ownership filings", weight: 22, perItem: 6 },
  { cat: "financial", re: /OFAC|sanctions|OpenSanctions|FBI Wanted|watchlist/i, label: "Sanctions / watchlist presence", weight: 40 },
  { cat: "financial", re: /CourtListener|PACER|court|docket|lawsuit/i, label: "Litigation records", weight: 25, perItem: 8 },
  { cat: "financial", re: /wallet|Ethplorer|Blockchain|crypto/i, label: "On-chain wallet activity", weight: 28, perItem: 6 },

  { cat: "metadata", re: /Favicon hash|Tech fingerprint|robots\.txt|sitemap\.xml|humans\.txt|ads\.txt/i, label: "Technical fingerprints", weight: 12 },
  { cat: "metadata", re: /OpenPGP|PGP/i, label: "Published key material", weight: 12 },
  { cat: "metadata", re: /commits by email|commit/i, label: "Email exposed in commit metadata", weight: 22, perItem: 5 },
  { cat: "metadata", re: /document|PDF|filetype|arXiv|OpenAlex|Crossref|GovInfo/i, label: "Documents carrying subject metadata", weight: 18, perItem: 4 },
  { cat: "metadata", re: /Wayback|Common Crawl|archive/i, label: "Archived copies of removed content", weight: 15, perItem: 4 },
];

function countItems(data: unknown): number {
  if (!data || typeof data !== "object") return 0;
  let best = 0;
  for (const v of Object.values(data as Record<string, unknown>)) {
    if (Array.isArray(v)) best = Math.max(best, v.length);
    else if (typeof v === "number" && Number.isFinite(v)) best = Math.max(best, Math.min(v, 50));
  }
  return best;
}

function grade(exposure: number): string {
  if (exposure < 12) return "A";
  if (exposure < 25) return "B";
  if (exposure < 45) return "C";
  if (exposure < 65) return "D";
  if (exposure < 82) return "E";
  return "F";
}

function bandOf(score: number): FootprintScore["band"] {
  if (score >= 780) return "minimal";
  if (score >= 700) return "low";
  if (score >= 620) return "moderate";
  if (score >= 520) return "elevated";
  return "severe";
}

/**
 * Compute the Digital Footprint Score from a scan's collectors and findings.
 */
export function computeFootprint(
  collectors: Collectorish[] | undefined,
  findings?: Array<{ severity?: string; title?: string }> | undefined,
): FootprintScore {
  const ok = (collectors ?? []).filter((c) => c && c.status === "ok");
  const buckets = new Map<FootprintCategoryId, FootprintSignal[]>();
  const covered = new Set<FootprintCategoryId>();

  for (const c of collectors ?? []) {
    for (const rule of RULES) {
      if (!rule.re.test(c.source ?? "")) continue;
      covered.add(rule.cat);
      if (c.status !== "ok") continue;
      const n = countItems(c.data);
      const extra = rule.perItem ? Math.min(rule.weight, rule.perItem * Math.max(0, n - 1)) : 0;
      const weight = Math.min(100, rule.weight + extra);
      const list = buckets.get(rule.cat) ?? [];
      list.push({
        label: rule.label,
        detail: c.summary ?? `${rule.label} observed.`,
        weight,
        source: c.source,
      });
      buckets.set(rule.cat, list);
      break;
    }
  }

  // Severe findings amplify the category they most plausibly belong to.
  for (const f of findings ?? []) {
    const sev = String(f?.severity ?? "").toLowerCase();
    if (sev !== "high" && sev !== "critical") continue;
    const title = String(f?.title ?? "");
    const cat: FootprintCategoryId = /breach|password|credential|leak/i.test(title)
      ? "breach"
      : /sanction|court|filing|wallet|financ/i.test(title)
      ? "financial"
      : /domain|host|dns|cert|ip |server/i.test(title)
      ? "infrastructure"
      : /profile|social|media|mention/i.test(title)
      ? "social"
      : "metadata";
    const list = buckets.get(cat) ?? [];
    list.push({
      label: `${sev === "critical" ? "Critical" : "High"} finding`,
      detail: title || "High-severity finding recorded by the scan.",
      weight: sev === "critical" ? 30 : 18,
      source: "Scan findings",
    });
    buckets.set(cat, list);
    covered.add(cat);
  }

  const categories: FootprintCategory[] = (Object.keys(CATEGORY_META) as FootprintCategoryId[]).map((id) => {
    const meta = CATEGORY_META[id];
    const signals = (buckets.get(id) ?? []).sort((a, b) => b.weight - a.weight);
    // Diminishing-returns aggregation: strongest signal counts fully, the rest taper.
    let exposure = 0;
    signals.forEach((s, i) => {
      exposure += s.weight * Math.pow(0.55, i);
    });
    exposure = Math.round(Math.max(0, Math.min(100, exposure)));
    return {
      id,
      label: meta.label,
      blurb: meta.blurb,
      exposure,
      grade: grade(exposure),
      signals,
      unmeasured: !covered.has(id),
      advice: exposure >= 25 ? meta.advice : "No material exposure detected in this category for the collected evidence.",
    };
  });

  const measured = categories.filter((c) => !c.unmeasured);
  const totalWeight = measured.reduce((a, c) => a + CATEGORY_META[c.id].weight, 0) || 1;
  const exposure = Math.round(
    measured.reduce((a, c) => a + c.exposure * CATEGORY_META[c.id].weight, 0) / totalWeight,
  );
  const score = Math.round(850 - (exposure / 100) * 550);
  const band = bandOf(score);
  const coverage = Math.round((measured.length / categories.length) * 100);
  const worst = [...categories].sort((a, b) => b.exposure - a.exposure)[0];

  const headline =
    ok.length === 0
      ? "Not enough collected evidence to rate this subject's online exposure."
      : band === "minimal"
      ? "Very small public footprint — little attributable exposure surfaced."
      : band === "low"
      ? `Contained footprint; ${worst?.label.toLowerCase()} is the main visible surface.`
      : band === "moderate"
      ? `Ordinary public footprint with a noticeable ${worst?.label.toLowerCase()} surface.`
      : band === "elevated"
      ? `Widely exposed subject — ${worst?.label.toLowerCase()} is driving the score down.`
      : `Severely exposed — ${worst?.label.toLowerCase()} plus corroborating surfaces make this subject highly targetable.`;

  return { score, exposure, grade: grade(exposure), band, headline, categories, coverage };
}
